import { AttributeRow, Badge, CollapsibleSection, Tooltip } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { CIVIL_DRAW_ROUTE } from '~/common/types';
import Icon from '~/components2/Icon';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import type { SidebarFieldRow, SidebarSection } from './buildSidebarSections';
import { categoryChipVariant } from './categoryChip';
import MetadataAttributeRow from './MetadataAttributeRow';

interface Props {
  section: SidebarSection;
  onBookmark: (section: SidebarSection) => void;
  onLinkOpen: (section: SidebarSection, row: SidebarFieldRow) => void;
  onHover: (section: SidebarSection) => void;
  onHoverEnd: () => void;
}

export default function MetadataSection({ section, onBookmark, onLinkOpen, onHover, onHoverEnd }: Props) {
  const [open, setOpen] = useState(true);
  const [hovered, setHovered] = useState(false);
  const { tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const isCivilDraw = tabId === CIVIL_DRAW_ROUTE;

  return (
    <CollapsibleSection
      title={section.title}
      open={open}
      onOpenChange={setOpen}
      hovered={hovered}
      data-testid={`metadata-sidebar-section-${section.id}`}
      toggleTestId={`metadata-sidebar-section-toggle-${section.id}`}
      onMouseEnter={() => {
        setHovered(true);
        onHover(section);
      }}
      onMouseLeave={() => {
        setHovered(false);
        onHoverEnd();
      }}
      badge={
        section.categoryLabel ? (
          <Badge color={categoryChipVariant(section.categoryLabel)}>{section.categoryLabel}</Badge>
        ) : undefined
      }
      actions={
        isCivilDraw && !section.isCustomData ? (
          <Tooltip asChild text="Bookmark attributes">
            <button
              type="button"
              aria-label="Bookmark attributes"
              data-testid={`metadata-sidebar-bookmark-${section.id}`}
              className="cursor-pointer rounded p-1 hover:bg-grey-20"
              onClick={() => onBookmark(section)}
            >
              <Icon icon="bookmark" size={16} />
            </button>
          </Tooltip>
        ) : undefined
      }
    >
      {section.isCustomData
        ? (section.customCategories ?? []).map((c, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: category labels aren't guaranteed unique
            <AttributeRow key={`${c}-${i}`} className="even:bg-grey-10" label="Category" value={c} />
          ))
        : section.fields.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: row.uniqueId isn't guaranteed unique across fragments
            <MetadataAttributeRow key={`${row.uniqueId}-${i}`} row={row} onLinkOpen={() => onLinkOpen(section, row)} />
          ))}
      {!section.isCustomData && section.fields.length === 0 && (
        <div className="px-2 py-2 text-xs italic text-grey-50">
          Detailed attributes are not available for this layer.
        </div>
      )}
    </CollapsibleSection>
  );
}
